import { validate as validateUUID, version as uuidVersion } from 'uuid';
import { encodeBigIntToBase62FixedLength } from './base62';

/**
 * Utilities for transforming IDs during cross-tenant import.
 *
 * There are two distinct ID “families” in the app:
 * - **Config entity IDs** (root `data.id` + JSON:API relationship IDs) are UUID-shaped.
 *   We transform these with {@link transformIdWithTenant} which *always* outputs a UUID v4.
 * - **Domain entity IDs** (`attributes.entityMetadata.domainEntityId` + schema-linked references)
 *   are not necessarily UUIDs (commonly 32-hex or 22-char alphanumeric). We transform these with
 *   {@link transformDomainEntityIdWithTenant} which preserves the original “shape”.
 *
 * Keeping these separate avoids a confusing API where “transformId” sometimes returns UUIDs,
 * sometimes hex strings, sometimes base62 strings, and has inconsistent reversibility guarantees.
 *
 * The transformation uses XOR with a hash of the tenant ID, while ensuring a valid UUID v4 Version and Variant bits. This ensures the resulting ID is:
 * 1. A valid UUID v4.
 * 2. Deterministic based on the pair (originalId, tenantId).
 *   - the same originalId and tenantId will always produce the same transformed ID.
 *   - different originalIds and the same tenantId will produce unique transformed IDs.
 *   - different tenantIds and the same originalId will produce unique transformed IDs.
 * 3. Reversible given the same tenantId and that the original ID is a valid UUID v4.
 *   - the transformed ID can be reverted to the original ID using the same tenantId and that the original ID is a valid UUID v4.
 *
 * Note: Accepts any UUID-formatted string (8-4-4-4-12 hex), including raw MD5 hashes
 * that don't have proper RFC4122 version/variant bits.
 */

// Constants for UUID v4 structure
const UUID_SIZE_BYTES = 16;
const UUID_VERSION_BYTE_INDEX = 6;
const UUID_VARIANT_BYTE_INDEX = 8;
const UUID_VERSION_4 = 0x40; // Version 4: 0100 xxxx
const UUID_VARIANT_RFC4122 = 0x80; // Variant 1: 10xx xxxx

/**
 * Validates if a string is a valid UUID v4
 * Format: 8-4-4-4-12 hex digits, version 4, variant 1 (8, 9, a, b)
 * xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx
 */
export const isUUIDv4 = (uuid: string): boolean =>
  validateUUID(uuid) && uuidVersion(uuid) === 4;

/**
 * Validates if a string has valid UUID format (8-4-4-4-12 hex digits)
 * This is more lenient than RFC4122 - it accepts any version/variant bits,
 * including raw MD5 hashes formatted as UUIDs.
 */
const UUID_FORMAT_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUUIDFormatLoose = (uuid: string): boolean =>
  UUID_FORMAT_REGEX.test(uuid);

/**
 * Parse a UUID-formatted string into bytes, even if it's not RFC4122 compliant.
 * Works with raw MD5 hashes formatted as UUIDs.
 */
const parseUUIDLoose = (uuid: string): Uint8Array => {
  if (!isValidUUIDFormatLoose(uuid)) {
    throw new Error(`Invalid UUID format: ${uuid}`);
  }

  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(UUID_SIZE_BYTES);
  for (let i = 0; i < UUID_SIZE_BYTES; i += 1) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
};

/**
 * Convert bytes to UUID string format (8-4-4-4-12)
 *
 * We use this instead of the uuid library's stringify because that function
 * validates RFC4122 compliance and throws on non-standard version/variant bits.
 * Since we accept non-RFC4122 inputs (like raw MD5 hashes) and force v4 bits
 * on output, we need a simple stringify that just formats without validation.
 */
const stringifyBytes = (bytes: Uint8Array): string => {
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16,
  )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
};

/**
 * Get the Web Crypto API from the current JS environment (browser, worker, or Node 18+ via globalThis)
 */
const getCryptoApi = (): Crypto | undefined =>
  typeof crypto !== 'undefined' ? crypto : undefined;

/**
 * Generates a deterministic 16-byte mask from a tenant ID using SHA-256.
 * The mask is used for XOR transformation to create tenant-specific UUIDs.
 *
 * The version and variant bit positions are zeroed out in the mask so that
 * XOR doesn't affect those positions. This allows us to force UUID v4
 * version/variant bits on the output while maintaining XOR reversibility.
 */
const getTenantMask = async (tenantId: string): Promise<Uint8Array> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(tenantId);

  const cryptoApi = getCryptoApi();

  if (!cryptoApi || !cryptoApi.subtle) {
    throw new Error(
      'Crypto API is not available. Cannot transform tenant ID safely.',
    );
  }

  // Generate SHA-256 hash of tenant ID (32 bytes)
  const hashBuffer = await cryptoApi.subtle.digest('SHA-256', data);
  const hashBytes = new Uint8Array(hashBuffer);

  // We only need 16 bytes for the mask (UUID size)
  const mask = hashBytes.slice(0, UUID_SIZE_BYTES);

  // Zero out version bits (Byte 6, high nibble)
  // This ensures XOR doesn't change the version position, so we can force v4 later
  // Without this, XOR would scramble these bits and break reversibility
  // eslint-disable-next-line no-bitwise
  mask[UUID_VERSION_BYTE_INDEX] &= 0x0f;

  // Zero out variant bits (Byte 8, high 2 bits)
  // This ensures XOR doesn't change the variant position, so we can force RFC4122 later
  // Without this, XOR would scramble these bits and break reversibility
  // eslint-disable-next-line no-bitwise
  mask[UUID_VARIANT_BYTE_INDEX] &= 0x3f;

  return mask;
};

/**
 * Transforms any UUID-formatted string into a valid UUID v4 based on the tenant ID.
 * This operation is symmetric: calling it twice with the same tenantId returns the
 * original ID (for v4 inputs) or a normalized v4 version (for non-v4 inputs).
 *
 * Expected input formats (common entityConfigId formats):
 * - UUID v4: f47ac10b-58cc-4372-a567-0e02b2c3d479 (version=4, variant=a)
 * - Raw MD5 hash: f8b26aa5-ff56-cc39-f397-108feb2d5cdc (version=c, variant=f)
 *
 * Order of operations:
 * 1. Parse input UUID to bytes
 * 2. XOR all bytes with tenant-derived mask (SHA-256 hash of tenantId)
 * 3. Force version=4 and variant=RFC4122 bits on the result
 *
 * On reverse (calling again with same tenantId):
 * 1. Parse transformed UUID to bytes
 * 2. XOR undoes the original transformation
 * 3. Force v4 bits again → returns original data with v4 bits (normalized)
 *
 * REVERSIBILITY: Only v4 inputs are reversible to exact original.
 * Non-v4 inputs (v1, v3, MD5 hashes) will be normalized to v4 on reversal.
 *
 * @param id The original or transformed UUID (any format with 8-4-4-4-12 hex digits)
 * @param tenantId The tenant ID to combine with the UUID
 * @returns A new UUID v4 string
 */
export const transformIdWithTenant = async (
  id: string,
  tenantId: string,
): Promise<string> => {
  if (!tenantId || tenantId.trim().length === 0) {
    throw new Error('Tenant ID cannot be empty');
  }
  const normalizedTenantId = tenantId.trim();
  if (!isValidUUIDFormatLoose(id)) {
    throw new Error(`Invalid UUID input: ${id}`);
  }

  const idBytes = parseUUIDLoose(id);
  const mask = await getTenantMask(normalizedTenantId);
  const resultBytes = new Uint8Array(UUID_SIZE_BYTES);

  for (let i = 0; i < UUID_SIZE_BYTES; i += 1) {
    // eslint-disable-next-line no-bitwise
    resultBytes[i] = idBytes[i] ^ mask[i];
  }

  // Force UUID v4 version and variant bits on the output.
  /* eslint-disable no-bitwise */
  // Version 4: set high nibble of byte 6 to 0100
  resultBytes[UUID_VERSION_BYTE_INDEX] =
    (resultBytes[UUID_VERSION_BYTE_INDEX] & 0x0f) | UUID_VERSION_4;
  // Variant RFC4122: set high 2 bits of byte 8 to 10
  resultBytes[UUID_VARIANT_BYTE_INDEX] =
    (resultBytes[UUID_VARIANT_BYTE_INDEX] & 0x3f) | UUID_VARIANT_RFC4122;
  /* eslint-enable no-bitwise */

  return stringifyBytes(resultBytes);
};

/**
 * ---------------------------------------------------------------------------
 * Domain-entity ID transformation (non-UUID IDs)
 * ---------------------------------------------------------------------------
 *
 * These IDs show up in:
 * - `attributes.entityMetadata.domainEntityId`
 * - Any schema path annotated with `x-entity-reference.domainEntityType`
 *   (and `x-entity-asset` media references, which are also treated as “linked refs”).
 *
 * This transformation is:
 * - deterministic for (tenantId, domainEntityId)
 * - not intended to be reversible
 * - format-preserving for supported formats (32-hex stays 32-hex; 22-char stays 22-char)
 *
 * TODO (future): if we ever want per-entity overrides (e.g., a domain type that uses a
 * different ID shape), consider adding the shape to entity-config - was not able to do this yet because we are observing multiple different formats for the same domainEntityType
 */
const DOMAIN_ENTITY_ID_HEX24_REGEX = /^[0-9a-f]{24}$/i;
const DOMAIN_ENTITY_ID_HEX32_REGEX = /^[0-9a-f]{32}$/i;
const DOMAIN_ENTITY_ID_SHORT_ALPHANUM_22_REGEX = /^[0-9A-Za-z]{22}$/;

/**
 * Encodes bytes into lower-case hex without separators.
 * Example: 16 bytes -> 32 hex chars.
 */
const encodeBytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

/**
 * Interprets bytes as an unsigned big-endian integer.
 * (Used only to base62-encode a 128-bit digest prefix.)
 */
const parseBigIntFromBytes = (bytes: Uint8Array): bigint =>
  bytes.reduce((acc, b) => acc * 256n + BigInt(b), 0n);

/**
 * Deterministically transforms a domainEntityId for import into another tenant while preserving format.
 *
 * Supported formats:
 * - 24-char hex (MongoDB ObjectId-style): `507f1f77bcf86cd799439011`
 * - 32-char hex (e.g. MD5-like IDs): `314630a7421ee40bab7acb10dd358d6e`
 * - 22-char alphanumeric (e.g. campaign/activity style): `OriyhBmNPdPj81VmWJLKyn`
 *
 * For unsupported formats, returns the input unchanged (e.g. `image-123`).
 *
 * Note: Unlike UUID transformation, this is NOT intended to be reversible.
 */
export const transformDomainEntityIdWithTenant = async (
  domainEntityId: string,
  tenantId: string,
): Promise<string> => {
  if (!tenantId || tenantId.trim().length === 0) {
    throw new Error('Tenant ID cannot be empty');
  }
  const normalizedTenantId = tenantId.trim();
  if (domainEntityId.length === 0) return domainEntityId;

  const isHex24 = DOMAIN_ENTITY_ID_HEX24_REGEX.test(domainEntityId);
  const isHex32 = DOMAIN_ENTITY_ID_HEX32_REGEX.test(domainEntityId);
  const isShortAlphanum22 =
    DOMAIN_ENTITY_ID_SHORT_ALPHANUM_22_REGEX.test(domainEntityId);

  if (!isHex24 && !isHex32 && !isShortAlphanum22) return domainEntityId;

  const cryptoApi = getCryptoApi();
  if (!cryptoApi || !cryptoApi.subtle) {
    throw new Error(
      'Crypto API is not available. Cannot transform tenant ID safely.',
    );
  }

  const encoder = new TextEncoder();
  // Domain separation: include a fixed prefix so this hash stream is distinct from
  // any other hashing we might do with (tenantId, id) elsewhere.
  // Note: domainEntityIds are treated as opaque/case-sensitive strings.
  const input = encoder.encode(
    `domain-entity-id:${normalizedTenantId}:${domainEntityId}`,
  );
  const hashBuffer = await cryptoApi.subtle.digest('SHA-256', input);
  const hashBytes = new Uint8Array(hashBuffer);

  // We only need:
  // - 96 bits (12 bytes) to output 24 hex chars (ObjectId-style)
  // - 128 bits (16 bytes) to output 32 hex chars (hex32) and base62(22) for short IDs
  const first12 = hashBytes.slice(0, 12);
  const first16 = hashBytes.slice(0, 16);

  if (isHex24) {
    return encodeBytesToHex(first12);
  }
  if (isHex32) {
    return encodeBytesToHex(first16);
  }

  // 22-char alphanumeric: encode 128 bits into base62 and left-pad to fixed length.
  const hashAsNumber = parseBigIntFromBytes(first16);
  return encodeBigIntToBase62FixedLength(hashAsNumber, 22);
};
